import type { VariantProps } from 'class-variance-authority'
import type { ComponentProps, CSSProperties } from 'react'
import { cva } from 'class-variance-authority'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ThemeContext } from './ThemeContext'

const buttonVariants = cva(
  'flex w-full cursor-pointer justify-center rounded-md hover:brightness-95 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'border border-[var(--button-bg-color)] bg-[var(--button-bg-color)] text-[var(--button-text-color)]',
        outline: 'border border-[#1B1C1B]',
      },
      size: {
        default: 'px-8 py-2 text-base',
        sm: 'px-4 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

type Props = ComponentProps<'button'> & VariantProps<typeof buttonVariants> & {
  throttleMs?: number
  isThrottling?: boolean
}

export function ThemeButton({
  variant,
  size,
  onClick,
  className,
  disabled,
  children,
  throttleMs,
}: Props) {
  // throttle関連
  const [isThrottling, setIsThrottling] = useState(false)
  const timeoutIdRef = useRef<number | null>(null)
  const lastClickTime = useRef<number>(0)
  const [prevThrottleMs, setPrevThrottleMs] = useState(throttleMs)

  if (throttleMs !== prevThrottleMs) {
    setPrevThrottleMs(throttleMs)
    lastClickTime.current = 0 // スロットリング時間をリセット
  }

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current !== null) {
        window.clearTimeout(timeoutIdRef.current)
      }
    }
  }, [])

  const handleClick: ComponentProps<'button'>['onClick'] = useCallback((...args: any) => {
    if (!onClick) {
      return
    }

    const params = args as Parameters<typeof onClick>

    if (!throttleMs) {
      onClick(...params)
      return
    }

    const now = Date.now()

    if (now - lastClickTime.current >= throttleMs) {
      // スロットリング期間が過ぎている場合
      lastClickTime.current = now
      setIsThrottling(true)
      onClick(...params)

      timeoutIdRef.current = window.setTimeout(() => {
        setIsThrottling(false)
      }, throttleMs)
    }
  }, [onClick, throttleMs])

  // theme関連
  const theme = useContext(ThemeContext)
  const themeStyle = useMemo<CSSProperties>(() => {
    return {
      '--button-bg-color': theme.mainColor,
      '--button-text-color': theme.mainTextColor,
    } as CSSProperties
  }, [theme])

  return (
    <button
      className={buttonVariants({ variant, size, className })}
      style={themeStyle}
      disabled={disabled || isThrottling}
      onClick={handleClick}
    >
      {children}
    </button>
  )
}
